import { useState } from "react";
import {
  AlertTriangle, ArrowUpRight, ChevronRight, Clock3, FileWarning, Lock, PackageSearch,
  ShieldAlert, X, Upload, CheckCircle2, CircleDot, Paperclip, Filter,
} from "lucide-react";
import { Badge, Card, Mono, toneBox, type Tone } from "@/components/ui";
import {
  AGING, BATCHES, BLOKE_LABEL, KPIS, MY_QUEUE, OLAYLAR, P85, RISERS, STATIONS, STATION_BATCH,
  type BlokeKodu, type RiserRow,
} from "@/lib/mock";
import { cn } from "@/lib/cn";
import { SurecRayi } from "@/components/surec-rayi";
import { cevir } from "@/lib/dil";

const blokeTone: Record<BlokeKodu, Tone> = {
  "DISPOSITION-BEKLIYOR": "warn",
  "YEDEK-PARCA": "bad",
  "FOUND-CONDITION": "bad",
  "DOKUMAN-EKSIK": "warn",
  "SERTIFIKA/KALIBRASYON": "warn",
  "SET-BEKLIYOR": "bad",
  "KIMLIK-DOGRULAMA": "hold",
};
const blokeIcon: Record<BlokeKodu, React.ElementType> = {
  "DISPOSITION-BEKLIYOR": Clock3,
  "YEDEK-PARCA": PackageSearch,
  "FOUND-CONDITION": AlertTriangle,
  "DOKUMAN-EKSIK": FileWarning,
  "SERTIFIKA/KALIBRASYON": ShieldAlert,
  "SET-BEKLIYOR": Lock,
  "KIMLIK-DOGRULAMA": ShieldAlert,
};

const kpiTone: Record<string, string> = {
  neutral: "text-ink",
  info: "text-info",
  ok: "text-ok",
  warn: "text-warn",
  bad: "text-bad",
  hold: "text-hold",
};

export function DashboardA() {
  const [secili, setSecili] = useState<RiserRow | null>(null);
  const [batchF, setBatchF] = useState<string>(cevir("All"));
  const [aktifIst, setAktifIst] = useState<string | null>(null);

  const riserlar = RISERS.filter((r) => (batchF === cevir("All") || r.batch === batchF) && (!aktifIst || r.istasyon === aktifIst));

  return (
    <div className="space-y-4">
      {/* Başlık + filtreler */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-ink-3">{cevir("E-01 · View A")}</div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{cevir("Command Center")}</h1>
          <p className="text-base text-ink-2">{cevir("DS Yavuz riser recertification · Filyos · 24 Aug 2026, 14:40 updated")}</p>
        </div>
        <div className="scroll-none flex max-w-full items-center gap-1.5 overflow-x-auto">
          <Filter size={14} className="shrink-0 text-ink-3" />
          {[cevir("All"), ...BATCHES.map((b) => b.no)].map((b) => (
            <button
              key={b}
              onClick={() => setBatchF(b)}
              aria-pressed={batchF === b}
              className={cn(
                "shrink-0 rounded-full border px-3 py-1 text-sm font-medium transition",
                batchF === b ? "border-navy bg-navy text-white dark:text-[#0a141c]" : "border-line bg-surface text-ink-2 hover:border-line-strong",
              )}
            >
              {b}
              {b !== cevir("All") && <span className="ml-1 opacity-70 tnum">{BATCHES.find((x) => x.no === b)?.riser}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Süreç rayı (melez: B'den) */}
      <div className="relative -mx-3 px-3 sm:mx-0 sm:px-0">
        <SurecRayi aktif={aktifIst} onSec={setAktifIst} font="plex" />
        {aktifIst && (
          <div className="mt-2 flex items-center gap-2 rounded-md border border-orange/40 bg-orange-soft px-3 py-2 text-sm">
            <span className="font-semibold">{aktifIst} · {STATIONS.find((s) => s.code === aktifIst)?.ad}</span>
            <span className="text-ink-2">{cevir("selected: the list and heat map below are filtered to this station.")}</span>
            <button onClick={() => setAktifIst(null)} className="ml-auto font-semibold text-orange">{cevir("Remove")}</button>
          </div>
        )}
      </div>

      {/* KPI şeridi */}
      <div className="scroll-thin -mx-3 grid auto-cols-[minmax(168px,1fr)] grid-flow-col gap-2.5 overflow-x-auto px-3 pb-1 sm:mx-0 sm:px-0 md:grid-flow-row md:grid-cols-3 md:overflow-visible lg:grid-cols-5 2xl:grid-cols-10">
        {KPIS.map((k) => (
          <button
            key={k.key}
            title={`${k.ad} — ${k.deger} ${k.alt} · ${k.not}`}
            className="group flex min-w-0 flex-col rounded-lg border border-line bg-surface p-3 text-left shadow-[var(--shadow)] transition hover:border-line-strong"
          >
            {/* Etiket iki satıra kadar; başlık yüksekliği sabit ki rakamlar kartlar arasında aynı hizada dursun. */}
            <div className="flex items-start justify-between gap-1">
              <span className="line-clamp-2 min-h-[2.1em] text-xs font-medium leading-tight text-ink-2">{k.ad}</span>
              <ArrowUpRight size={13} className="shrink-0 text-ink-3 opacity-0 transition group-hover:opacity-100" />
            </div>
            <div className="mt-1.5 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
              <span className={cn("text-3xl font-semibold leading-none tnum", kpiTone[k.ton])}>{k.deger}</span>
              <span className="min-w-0 break-words text-xs leading-tight text-ink-3">{k.alt}</span>
            </div>
            <div className="mt-auto pt-1.5 text-2xs leading-snug text-ink-3">{k.not}</div>
          </button>
        ))}
      </div>

      {/* Isı haritası + Dikkat */}
      <div className="grid gap-4 xl:grid-cols-12">
        <Card
          className="xl:col-span-8"
          title={cevir("Station × batch · component count")}
          action={<span className="hidden sm:inline">{cevir("Click cell → E-05 filtered grid")}</span>}
          padded={false}
        >
          <div className="scroll-thin overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-surface px-3 py-2 text-left font-medium text-ink-3">Batch</th>
                  {STATIONS.map((s) => (
                    <th key={s.code} className="px-1 py-2 text-center font-medium text-ink-3">
                      <div className="font-mono text-2xs text-ink-3">{s.code}</div>
                      <div className="text-xs font-medium text-ink-2">{s.kisa}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {BATCHES.map((b) => (
                  <tr key={b.no} className="border-t border-line">
                    <td className="sticky left-0 z-10 bg-surface px-3 py-1.5">
                      <div className="font-semibold">{b.no}</div>
                      <div className="text-2xs text-ink-3 tnum">{b.teslim}/{b.riser} riser · WO {b.wo}</div>
                    </td>
                    {STATION_BATCH[b.no]!.map((n, i) => {
                      const max = 52;
                      const a = n === 0 ? 0 : 0.12 + (n / max) * 0.78;
                      const na = b.no === "B4" && i > 1;
                      return (
                        <td key={i} className="p-0.5">
                          <button
                            className={cn(
                              "flex h-11 w-full items-center justify-center rounded-md border border-transparent text-sm font-semibold tnum transition hover:border-orange",
                              n === 0 && "text-ink-3/60",
                            )}
                            style={n ? { background: `color-mix(in oklab, var(--brand-navy) ${Math.round(a * 100)}%, var(--surface))`, color: a > 0.5 ? "#fff" : "var(--text)" } : undefined}
                            title={`${b.no} · ${STATIONS[i]!.ad}: ${n} komponent`}
                          >
                            {n === 0 ? (na ? "·" : "0") : n}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line px-3 py-2 text-xs text-ink-3">
            <span>{cevir("No S7 Hydrotest and PWHT in MB; no S8 coating in H. N/A lines are excluded from denominator.")}</span>
            <span className="ml-auto flex items-center gap-1">
              <i className="inline-block h-2.5 w-6 rounded-sm" style={{ background: "color-mix(in oklab, var(--brand-navy) 15%, var(--surface))" }} /> {cevir("few")}
              <i className="inline-block h-2.5 w-6 rounded-sm bg-navy" /> {cevir("many")}
            </span>
          </div>
        </Card>

        <Card className="xl:col-span-4" title={cevir("Attention · blocked Items")} action={<span>{RISERS.filter((r) => r.bloke).length} riser</span>} padded={false}>
          <ul className="divide-y divide-line">
            {RISERS.filter((r) => r.bloke).map((r) => {
              const Icon = blokeIcon[r.bloke!];
              return (
                <li key={r.rn}>
                  <button onClick={() => setSecili(r)} className="flex w-full items-start gap-3 px-4 py-2.5 text-left hover:bg-surface-2">
                    <span className={cn("mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md", toneBox[blokeTone[r.bloke!]])}>
                      <Icon size={15} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <Mono className="font-semibold text-ink">{r.rn}</Mono>
                        <Badge tone={blokeTone[r.bloke!]}>{BLOKE_LABEL[r.bloke!]}</Badge>
                        <span className="ml-auto text-xs text-ink-3 tnum">{r.gun} {cevir("d")}</span>
                      </span>
                      <span className="mt-0.5 block truncate text-sm text-ink-2">{r.bekleyen}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>

      {/* Aging + kuyruk + olaylar */}
      <div className="grid gap-4 lg:grid-cols-12">
        <Card className="lg:col-span-5" title={cevir("Aging WIP · days spent at station")} action={<span>{cevir("dot = component · line = p85")}</span>}>
          <AgingChart />
        </Card>

        <Card className="lg:col-span-4" title={cevir("My queue · Erdem Ulu")} action={<span>{MY_QUEUE.length} {cevir("tasks")}</span>} padded={false}>
          <ul className="divide-y divide-line">
            {MY_QUEUE.map((q) => (
              <li key={q.id} className="flex items-start gap-3 px-4 py-2.5 hover:bg-surface-2">
                <span className={cn("mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md", q.tip === "onay" ? "bg-hold-bg text-hold" : q.tip === "yukle" ? "bg-info-bg text-info" : q.tip === "karar" ? "bg-warn-bg text-warn" : "bg-surface-2 text-ink-2")}>
                  {q.tip === "onay" ? <CheckCircle2 size={14} /> : q.tip === "yukle" ? <Upload size={14} /> : q.tip === "karar" ? <CircleDot size={14} /> : <Paperclip size={14} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 text-base font-medium">
                    <span className="truncate">{q.baslik}</span>
                    {q.acil && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-orange" />}
                  </span>
                  <span className="mt-0.5 flex items-center gap-2 text-xs text-ink-3">
                    {q.kapi && <Badge tone="neutral">{q.kapi}</Badge>}
                    <span className="truncate">{q.alt}</span>
                    <span className="ml-auto shrink-0 tnum">{q.yas}</span>
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="lg:col-span-3" title={cevir("Recent events")} action={<span>{cevir("audit trail")}</span>} padded={false}>
          <ul className="divide-y divide-line">
            {OLAYLAR.map((o, i) => (
              <li key={i} className="px-4 py-2.5">
                <div className="flex items-center gap-2 text-xs text-ink-3">
                  <Mono>{o.ts}</Mono>
                  <span className={cn("truncate", o.tip === "sistem" && "text-hold")}>{o.kim}</span>
                </div>
                <div className="mt-0.5 text-sm leading-snug text-ink-2">{o.ne}</div>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* Riser listesi */}
      <Card title={`Riser'lar · ${riserlar.length}`} action={<span className="hidden sm:inline">{cevir("Navigate with ↑↓ / J K · Enter opens")}</span>} padded={false}>
        <div className="scroll-thin overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="text-xs text-ink-3">
              <tr className="border-b border-line">
                <th className="px-4 py-2 text-left font-medium">RN</th>
                <th className="px-2 py-2 text-left font-medium">Assembly SN</th>
                <th className="px-2 py-2 text-left font-medium">Batch</th>
                <th className="px-2 py-2 text-left font-medium">{cevir("Station")}</th>
                <th className="px-2 py-2 text-left font-medium">{cevir("Lines")}</th>
                <th className="px-2 py-2 text-left font-medium">{cevir("Bottleneck")}</th>
                <th className="px-2 py-2 text-right font-medium">{cevir("Day")}</th>
                <th className="px-2 py-2 text-left font-medium">{cevir("Blocked")}</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {riserlar.map((r) => (
                <tr
                  key={r.rn}
                  onClick={() => setSecili(r)}
                  className={cn("cursor-pointer border-b border-line last:border-0 hover:bg-surface-2", secili?.rn === r.rn && "bg-orange-soft/60 hover:bg-orange-soft/60")}
                >
                  <td className="px-4 py-2"><Mono className="font-semibold text-ink">{r.rn}</Mono></td>
                  <td className="px-2 py-2"><Mono className="text-ink-2">{r.sn}</Mono></td>
                  <td className="px-2 py-2 text-ink-2">{r.batch}</td>
                  <td className="px-2 py-2">
                    <span className="inline-flex items-center gap-1.5">
                      <Mono className="text-ink-3">{r.istasyon}</Mono>
                      <span className="text-ink">{STATIONS.find((s) => s.code === r.istasyon)?.ad}</span>
                    </span>
                  </td>
                  <td className="px-2 py-2"><HatCubes hatlar={r.hatlar} /></td>
                  <td className="px-2 py-2 text-ink-2">{r.darbogaz}</td>
                  <td className={cn("px-2 py-2 text-right tnum", r.gun > 14 ? "font-semibold text-bad" : "text-ink-2")}>{r.gun}</td>
                  <td className="px-2 py-2">{r.bloke ? <Badge tone={blokeTone[r.bloke]} dot>{BLOKE_LABEL[r.bloke]}</Badge> : <span className="text-ink-3">—</span>}</td>
                  <td className="px-2 py-2 text-ink-3"><ChevronRight size={14} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detay paneli */}
      {secili && <DetayPanel r={secili} onClose={() => setSecili(null)} />}
    </div>
  );
}

function HatCubes({ hatlar }: { hatlar: RiserRow["hatlar"] }) {
  const cls = { ok: "bg-ok", islemde: "bg-info", bloke: "bg-bad", hold: "bg-hold", na: "bg-surface-3" };
  return (
    <span className="inline-flex items-center gap-1">
      {hatlar.map((h) => (
        <span key={h.hat} title={`${h.hat} · ${h.istasyon} · ${h.durum}`} className={cn("flex h-5 w-6 items-center justify-center rounded-[4px] text-2xs font-bold text-white", cls[h.durum])}>
          {h.hat}
        </span>
      ))}
    </span>
  );
}

function AgingChart() {
  const W = 520, H = 190, padL = 28, padB = 26, padT = 10;
  const cols = STATIONS.length;
  const cw = (W - padL) / cols;
  const maxG = 65;
  const y = (g: number) => padT + (H - padB - padT) * (1 - Math.min(g, maxG) / maxG);
  return (
    <div className="w-full overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label={cevir("Aging WIP chart")}>
        {[0, 15, 30, 45, 60].map((g) => (
          <g key={g}>
            <line x1={padL} x2={W} y1={y(g)} y2={y(g)} stroke="var(--line)" strokeDasharray={g ? "2 3" : undefined} />
            <text x={padL - 6} y={y(g) + 3.5} textAnchor="end" fontSize="9.5" fill="var(--text-3)" className="tnum">{g}</text>
          </g>
        ))}
        {STATIONS.map((s, i) => {
          const p = P85[i];
          const x0 = padL + i * cw;
          return (
            <g key={s.code}>
              <text x={x0 + cw / 2} y={H - 8} textAnchor="middle" fontSize="9.5" fill="var(--text-3)" fontFamily="var(--font-mono)">{s.code}</text>
              {p !== undefined && <line x1={x0 + 6} x2={x0 + cw - 6} y1={y(p)} y2={y(p)} stroke="var(--brand-orange)" strokeWidth={1.5} />}
            </g>
          );
        })}
        {AGING.map((a, i) => {
          const x0 = padL + a.s * cw;
          const jitter = ((i * 37) % 11) - 5;
          return (
            <circle
              key={i}
              cx={x0 + cw / 2 + jitter * 1.4}
              cy={y(a.g)}
              r={4}
              fill={a.late ? "var(--bad)" : "var(--brand-navy)"}
              stroke="var(--surface)"
              strokeWidth={1.5}
              opacity={0.9}
            >
              <title>{STATIONS[a.s]!.ad}: {a.g} {cevir("days")}{a.late ? cevir(" · over p85") : ""}</title>
            </circle>
          );
        })}
      </svg>
      <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-ink-3">
        <span className="inline-flex items-center gap-1"><i className="h-2 w-2 rounded-full bg-navy" /> {cevir("at station")}</span>
        <span className="inline-flex items-center gap-1"><i className="h-2 w-2 rounded-full bg-bad" /> {cevir("over p85 (delayed)")}</span>
        <span className="inline-flex items-center gap-1"><i className="h-0.5 w-4 bg-orange" /> {cevir("p85 threshold")}</span>
        <span className="ml-auto">{cevir("40 d in S6: R061 MB · 61 d in S11/SERT: R106 COC waiting")}</span>
      </div>
    </div>
  );
}

function DetayPanel({ r, onClose }: { r: RiserRow; onClose: () => void }) {
  const s = STATIONS.find((x) => x.code === r.istasyon)!;
  const idx = STATIONS.findIndex((x) => x.code === r.istasyon);
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={onClose} />
      <aside className="fixed inset-x-0 bottom-0 z-50 max-h-[88dvh] overflow-y-auto rounded-t-2xl border-t border-line bg-surface shadow-[var(--shadow-lg)] lg:inset-y-0 lg:left-auto lg:right-0 lg:max-h-none lg:w-[420px] lg:rounded-none lg:border-l lg:border-t-0">
        <div className="sticky top-0 flex items-center gap-3 border-b border-line bg-surface/95 px-4 py-3 backdrop-blur">
          <div className="mx-auto h-1 w-10 rounded-full bg-line-strong lg:hidden" />
          <div className="hidden lg:block">
            <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">{cevir("Riser 360 · preview")}</div>
            <div className="flex items-center gap-2"><Mono className="text-lg font-semibold text-ink">{r.rn}</Mono><Mono className="text-ink-3">{r.sn}</Mono></div>
          </div>
          <button onClick={onClose} className="ml-auto rounded-md p-1.5 text-ink-2 hover:bg-surface-2" aria-label={cevir("Close")}><X size={18} /></button>
        </div>
        <div className="space-y-4 p-4">
          <div className="lg:hidden">
            <div className="flex items-center gap-2"><Mono className="text-lg font-semibold text-ink">{r.rn}</Mono><Mono className="text-ink-3">{r.sn}</Mono></div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="rounded-md bg-surface-2 p-2"><div className="text-2xs text-ink-3">{cevir("Station")}</div><div className="font-semibold">{s.code} · {s.kisa}</div></div>
            <div className="rounded-md bg-surface-2 p-2"><div className="text-2xs text-ink-3">{cevir("How many days")}</div><div className={cn("font-semibold tnum", r.gun > 14 && "text-bad")}>{r.gun} {cevir("days")}</div></div>
            <div className="rounded-md bg-surface-2 p-2"><div className="text-2xs text-ink-3">Batch / WO</div><div className="font-semibold">{r.batch} · {BATCHES.find((b) => b.no === r.batch)?.wo}</div></div>
          </div>

          {r.bloke && (
            <div className={cn("rounded-md border p-3 text-sm", toneBox[blokeTone[r.bloke]])}>
              <div className="flex items-center gap-2 font-semibold"><Lock size={14} /> {BLOKE_LABEL[r.bloke]} · {r.bloke}</div>
              <div className="mt-1 text-ink-2">{r.bekleyen}</div>
            </div>
          )}

          <div>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-ink-3">{cevir("Phase sequence")}</div>
            <div className="flex gap-0.5">
              {STATIONS.map((st, i) => (
                <span key={st.code} title={st.ad} className={cn("h-2 flex-1 rounded-sm", i < idx ? "bg-ok" : i === idx ? (r.bloke ? "bg-bad" : "bg-info") : "bg-surface-3")} />
              ))}
            </div>
            <div className="mt-1 flex justify-between text-2xs text-ink-3"><span>S1</span><span>S6</span><span>S11 · SERT</span></div>
          </div>

          <div>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-ink-3">{cevir("5 lines")}</div>
            <ul className="divide-y divide-line rounded-md border border-line">
              {r.hatlar.map((h) => (
                <li key={h.hat} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className={cn("flex h-6 w-8 items-center justify-center rounded text-2xs font-bold text-white", { ok: "bg-ok", islemde: "bg-info", bloke: "bg-bad", hold: "bg-hold", na: "bg-surface-3" }[h.durum])}>{h.hat}</span>
                  <span className="flex-1">{STATIONS.find((s2) => s2.code === h.istasyon)?.ad}</span>
                  <Badge tone={{ ok: "ok", islemde: "info", bloke: "bad", hold: "hold", na: "neutral" }[h.durum] as Tone}>{{ ok: "tamam", islemde: cevir("in progress"), bloke: "bloke", hold: cevir("Awaiting G1"), na: "N/A" }[h.durum]}</Badge>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-ink-3">Dossier</div>
            <div className="grid grid-cols-4 gap-1.5 text-2xs">
              {["FM-0011", "SURVREP", "Dispo.", "Fit-up", "Weld map", "NDT", "QAF 246", "FM-0012"].map((d, i) => (
                <span key={d} className={cn("rounded-md border px-1.5 py-1.5 text-center", i < 3 ? "border-transparent bg-ok-bg text-ok" : i < 6 ? "border-transparent bg-info-bg text-info" : "border-dashed border-line text-ink-3")}>{d}</span>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <button className="flex-1 rounded-md bg-orange px-3 py-2 text-base font-semibold text-white hover:opacity-90">{cevir("Open Riser 360")}</button>
            <button className="rounded-md border border-line px-3 py-2 text-base text-ink-2 hover:bg-surface-2">Pin</button>
          </div>
        </div>
      </aside>
    </>
  );
}
